import React from 'react';
import { motion } from 'framer-motion';

interface GlowingButtonProps {
  children: React.ReactNode;
  onClick?: (e?: any) => void;
  href?: string;
  className?: string;
  variant?: 'primary' | 'secondary' | 'none' | string;
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  download?: string | boolean;
  target?: string;
  rel?: string;
  [key: string]: any; // Allow other standard HTML attributes
}

const GlowingButton: React.FC<GlowingButtonProps> = ({
  children,
  onClick,
  href,
  className = '',
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  download,
  ...rest
}) => {
  const baseClasses = 'cta-button relative inline-flex items-center justify-center font-semibold transition-all duration-300 cursor-pointer overflow-hidden';
  
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };

  const variantClasses: Record<string, string> = {
    primary: 'bg-blue-900 text-white',
    secondary: 'bg-purple-900 text-white'
  };

  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant] || ''} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    
    if (onClick) {
      // If it's a download link, we want to allow the default behavior 
      // so the file actually downloads while we record the click.
      if (!download) {
        e.preventDefault();
      }
      onClick();
    } else if (href && !href.startsWith('/') && !href.startsWith('http')) {
      // Scroll to element if it's an anchor link like #features
      const element = document.querySelector(href);
      if (element) {
        e.preventDefault();
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // If it's a normal link or download, let the anchor handle it
  };

  if (href) {
    return (
      <motion.a
        href={href}
        className={buttonClasses}
        onClick={handleClick as any}
        download={download}
        whileHover={disabled ? {} : { y: -2, scale: 1.02 }}
        whileTap={disabled ? {} : { scale: 0.98 }}
        transition={{ duration: 0.2 }}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...(rest as any)}
      >
        {children}
      </motion.a>
    );
  }

  return (
    <motion.button
      type={type}
      className={buttonClasses}
      onClick={handleClick as any}
      disabled={disabled}
      whileHover={disabled ? {} : { y: -2, scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
      transition={{ duration: 0.2 }}
      {...(rest as any)}
    >
      {children}
    </motion.button>
  );
};

export default GlowingButton;
